// PendingPage — pending-bookings queue (legacy pending_bookings.php).
//
// Filter state lives in URL search params (CLIENT-DECISION-010): ?q=,
// ?status=, ?page=, ?limit=. Search is debounced; status filters are pill
// buttons (All / Pending / Final Notice) matching the legacy display.
//
// Stats strip: 4 summary stats above the table (Total Pending, Final
// Notice, Total Value, Added Today) derived from a separate full-list
// fetch (`usePendingList({ page:1, limit:500 })`). Pending queues are
// typically small so a single page suffices for accurate counters.
//
// Row actions mirror the legacy 7-icon column: View / Edit / Notes /
// Follow-up / Final Notice / Book In / Cancel / Delete (admin-only).
//
// Note: pending_bookings rows have no driver until they're booked in, and
// no collection_date — `created_at` IS the "date added".

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightCircle,
  Eye,
  Pencil,
  Plus,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { FilterBar } from '../../components/ui/FilterBar/FilterBar';
import { StatsStrip } from '../../components/ui/StatsStrip/StatsStrip';
import { Table, type Column, type RowAction } from '../../components/ui/Table/Table';
import { Pagination } from '../../components/ui/Pagination/Pagination';
import { Badge } from '../../components/ui/Badge/Badge';
import type { BadgeVariant } from '../../components/ui/Badge/Badge';
import { Button } from '../../components/ui/Button/Button';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useAuthStore } from '../../store/authStore';
import { usePendingList, type PendingFilters } from './hooks/usePendingList';
import { ConvertPendingModal } from './components/ConvertPendingModal';
import { FollowupPendingModal, type FollowupTab } from './components/FollowupPendingModal';
import { CancelPendingModal } from './components/CancelPendingModal';
import { StatusChangePendingModal } from './components/StatusChangePendingModal';
import { DeletePendingConfirm } from './components/DeletePendingConfirm';
import { queryKeys } from '../../api/queryKeys';
import type {
  PendingListResponse,
  PendingStatus,
  PendingSummary,
} from '../../types/api';
import styles from './PendingPage.module.css';

const STATUS_FILTERS: ReadonlyArray<{
  value: '' | 'pending' | 'final_notice';
  label: string;
}> = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'final_notice', label: 'Final Notice' },
];

const STATUS_VARIANTS: Record<PendingStatus, BadgeVariant> = {
  pending: 'warning',
  final_notice: 'danger',
  booked: 'success',
  cancelled: 'muted',
};

const STATUS_LABELS: Record<PendingStatus, string> = {
  pending: 'Pending',
  final_notice: 'Final Notice',
  booked: 'Booked',
  cancelled: 'Cancelled',
};

const DEFAULT_LIMIT = 50;
const KPI_LIMIT = 500;

function daysBetween(iso: string): number {
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return 0;
  const ms = Date.now() - start.getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

function daysClass(days: number): string {
  if (days >= 8) return styles.daysDanger ?? '';
  if (days >= 4) return styles.daysWarning ?? '';
  return styles.daysDefault ?? '';
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatPrice(p: number | null): string {
  if (p === null) return '—';
  return `£${p.toFixed(2)}`;
}

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface KpiCounts {
  pending: number;
  final_notice: number;
  total_value: number;
  added_today: number;
}

function deriveKpis(rows: PendingSummary[]): KpiCounts {
  const today = todayKey();
  const counts: KpiCounts = {
    pending: 0,
    final_notice: 0,
    total_value: 0,
    added_today: 0,
  };
  for (const r of rows) {
    if (r.status === 'pending') counts.pending += 1;
    if (r.status === 'final_notice') counts.final_notice += 1;
    if (r.price !== null) counts.total_value += r.price;
    if (r.created_at.startsWith(today)) counts.added_today += 1;
  }
  return counts;
}

export function PendingPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const q = params.get('q') ?? '';
  const status = (params.get('status') ?? '') as PendingFilters['status'] | '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const limit = Math.max(
    1,
    Number.parseInt(params.get('limit') ?? String(DEFAULT_LIMIT), 10) || DEFAULT_LIMIT,
  );

  const filters: PendingFilters = useMemo(() => {
    const f: PendingFilters = { page, limit };
    if (q.trim()) f.q = q.trim();
    if (status) f.status = status;
    return f;
  }, [q, status, page, limit]);

  const list = usePendingList(filters);

  // KPI fetch — separate query, no filters, large page so the four
  // counters are accurate even when the user has narrowed the table view.
  const kpiQuery = useQuery<PendingListResponse>({
    queryKey: queryKeys.pending.list({ kpi: true }),
    queryFn: async () => {
      const res = await apiClient.get<PendingListResponse>(endpoints.pending.list, {
        params: { page: 1, limit: KPI_LIMIT },
      });
      if (!res.data) throw new Error('KPI fetch missing data');
      return res.data;
    },
    staleTime: 30_000,
  });
  const kpis = useMemo(
    () => deriveKpis(kpiQuery.data?.pending_bookings ?? []),
    [kpiQuery.data],
  );

  // Per-row modal targets. Only one modal is open at a time; clicking a
  // row action sets the corresponding state slot and closes any others.
  const [convertTarget, setConvertTarget] = useState<PendingSummary | null>(null);
  const [followupTarget, setFollowupTarget] = useState<{
    row: PendingSummary;
    tab: FollowupTab;
  } | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PendingSummary | null>(null);
  const [statusTarget, setStatusTarget] = useState<PendingSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PendingSummary | null>(null);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  function refreshAll() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.pending.all });
  }

  const columns: Column<PendingSummary>[] = useMemo(
    () => [
      {
        key: 'created_at',
        header: 'Date',
        render: (_v, row) => (
          <span className={styles.cellMono}>{formatDate(row.created_at)}</span>
        ),
      },
      {
        key: 'car_registration',
        header: 'Registration',
        render: (_v, row) => (
          <span className={styles.regChip}>{row.car_registration}</span>
        ),
      },
      {
        key: 'name',
        header: 'Customer',
        render: (_v, row) => (
          <div className={styles.cellStack}>
            <span>{row.name}</span>
            <span className={styles.cellMuted}>{row.phone}</span>
          </div>
        ),
      },
      {
        key: 'address',
        header: 'Address',
        render: (_v, row) => (
          <div className={styles.cellStack}>
            <span>{row.address}</span>
            <span className={styles.cellMuted}>{row.postcode}</span>
          </div>
        ),
      },
      {
        key: 'product_name',
        header: 'Type',
        render: (_v, row) => row.product_name ?? '—',
      },
      {
        key: 'price',
        header: 'Price',
        align: 'right',
        render: (_v, row) => formatPrice(row.price),
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) => {
          const clickable = row.status === 'pending' || row.status === 'final_notice';
          return clickable ? (
            <button
              type="button"
              className={styles.statusButton}
              onClick={() => setStatusTarget(row)}
              aria-label="Change status"
            >
              <Badge variant={STATUS_VARIANTS[row.status]}>
                {STATUS_LABELS[row.status]}
              </Badge>
            </button>
          ) : (
            <Badge variant={STATUS_VARIANTS[row.status]}>
              {STATUS_LABELS[row.status]}
            </Badge>
          );
        },
      },
      {
        // Distinct key from the "Date" column above — both columns derive
        // from `row.created_at`, but Table uses the column key as the
        // React key for <th> / <td>, so duplicates would warn. Using
        // `updated_at` here is purely a unique-key placeholder; the
        // render still uses created_at.
        key: 'updated_at',
        header: 'Days Pending',
        align: 'right',
        render: (_v, row) => {
          const days = daysBetween(row.created_at);
          return (
            <span className={daysClass(days)}>
              {days === 0 ? 'Today' : `${days}d`}
            </span>
          );
        },
      },
    ],
    [],
  );

  // Row actions intentionally kept tight (5–6 inline actions, matching the
  // receipts list density). The legacy 7-icon column had two functional
  // duplicates which are removed here:
  //   - "Notes" was a deep-link to the detail page's notes panel — folded
  //     into the View action since the detail page already shows notes.
  //   - "Final Notice" opened the same modal as "Follow-up" with a different
  //     starting tab — folded into Follow-up; the modal's tab switcher
  //     handles the choice.
  const rowActions = (row: PendingSummary): RowAction<PendingSummary>[] => {
    const isTerminal = row.status === 'booked' || row.status === 'cancelled';
    const actions: RowAction<PendingSummary>[] = [
      {
        label: 'Book in',
        icon: ArrowRightCircle,
        variant: 'primary',
        onClick: () => setConvertTarget(row),
        hidden: () => isTerminal,
      },
      {
        label: 'View',
        icon: Eye,
        variant: 'ghost',
        onClick: () => navigate(`/collections/pending/${encodeURIComponent(row.enc_id)}`),
      },
      {
        label: 'Edit',
        icon: Pencil,
        variant: 'ghost',
        onClick: () => navigate(`/collections/pending/${encodeURIComponent(row.enc_id)}/edit`),
        hidden: () => isTerminal,
      },
      {
        label: 'Follow-up',
        icon: Send,
        variant: 'ghost',
        onClick: () => setFollowupTarget({ row, tab: 'regular' }),
        hidden: () => isTerminal,
      },
      {
        label: 'Cancel',
        icon: X,
        variant: 'danger',
        onClick: () => setCancelTarget(row),
        hidden: () => isTerminal,
      },
    ];
    if (isAdmin) {
      actions.push({
        label: 'Delete',
        icon: Trash2,
        variant: 'danger',
        onClick: () => setDeleteTarget(row),
      });
    }
    return actions;
  };

  const total = list.data?.pagination.total ?? 0;
  const totalPages = list.data?.pagination.totalPages ?? 1;
  const data = list.data?.pending_bookings ?? [];

  return (
    <div className={styles.page}>
      <PageHeader
        title={
          <span className={styles.titleRow}>
            Pending
            {total > 0 ? (
              <Badge variant="muted" className={styles.countBadge}>
                {total}
              </Badge>
            ) : null}
          </span>
        }
        subtitle="Manage and follow up on pending vehicle bookings"
        actions={
          <Button variant="primary" icon={Plus} onClick={() => navigate('/collections/pending/new')}>
            Add Pending Booking
          </Button>
        }
      />

      <StatsStrip
        ariaLabel="Pending booking counters"
        tiles={[
          { key: 'pending', label: 'Total Pending', value: kpis.pending },
          { key: 'final-notice', label: 'Final Notice', value: kpis.final_notice },
          { key: 'value', label: 'Total Value', value: `£${kpis.total_value.toFixed(0)}` },
          { key: 'today', label: 'Added Today', value: kpis.added_today },
        ]}
      />

      <Card flush>
        <FilterBar
          search={{
            value: q,
            onChange: (v) => setParam('q', v),
            placeholder: 'Search by name, phone, email, or registration…',
            ariaLabel: 'Search pending bookings',
          }}
        >
          <div className={styles.statusPills} role="tablist" aria-label="Filter by status">
            {STATUS_FILTERS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={status === opt.value}
                className={status === opt.value ? styles.pillActive : styles.pill}
                onClick={() => setParam('status', opt.value === '' ? null : opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </FilterBar>
        <Table
          columns={columns}
          data={data}
          keyField={(row) => row.enc_id}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <h3>No pending bookings</h3>
              <p>
                When a customer is added to the pending queue, the row appears
                here.
              </p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel="Pending bookings"
        />
        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit, total, totalPages }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>

      <ConvertPendingModal
        open={convertTarget !== null}
        pending={convertTarget}
        onClose={() => setConvertTarget(null)}
        onConverted={() => {
          refreshAll();
          setConvertTarget(null);
        }}
      />
      <FollowupPendingModal
        open={followupTarget !== null}
        encId={followupTarget?.row.enc_id ?? null}
        reference={followupTarget?.row.reference ?? null}
        initialTab={followupTarget?.tab ?? 'regular'}
        onClose={() => setFollowupTarget(null)}
        onSent={() => {
          refreshAll();
        }}
      />
      <CancelPendingModal
        open={cancelTarget !== null}
        encId={cancelTarget?.enc_id ?? null}
        reference={cancelTarget?.reference ?? null}
        onClose={() => setCancelTarget(null)}
        onCancelled={() => {
          refreshAll();
          setCancelTarget(null);
        }}
      />
      <StatusChangePendingModal
        open={statusTarget !== null}
        encId={statusTarget?.enc_id ?? null}
        currentStatus={statusTarget?.status ?? null}
        onClose={() => setStatusTarget(null)}
        onChanged={() => {
          refreshAll();
          setStatusTarget(null);
        }}
      />
      <DeletePendingConfirm
        open={deleteTarget !== null}
        encId={deleteTarget?.enc_id ?? null}
        reference={deleteTarget?.reference ?? null}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          refreshAll();
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}

